import {
  ChangeDetectionStrategy,
  Component,
  computed,
  input,
  model,
  viewChild,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import {
  SHAREABLE_TYPE_DISPLAY_NAME,
  ShareableType,
} from '@bindoc-projects/resource-domain-lib';
import { Button } from 'primeng/button';
import { MultiSelect } from 'primeng/multiselect';
import { Popover } from 'primeng/popover';

import {
  RESOURCE_PICKER_CRITERIA_EMPTY,
  ResourcePickerCriteria,
  resourcePickerCriteriaActiveCount,
} from './resource-picker.models';

interface TypeOption {
  readonly label: string;
  readonly value: ShareableType;
}

/**
 * Styleguide `FILTER_THRESHOLD` (`.claude/docs/styleguide/forms.md`): a list is
 * only worth a search field once it holds more than three options.
 */
const FILTER_THRESHOLD = 3;

/**
 * Narrowing panel of {@link BdResourcePickerComponent}: a popover holding the
 * picker's search facets plus a reset action. The criteria object is a two-way
 * {@link criteria} model, so the host stays thin — it owns the signal, renders
 * its own trigger button, calls {@link toggle} and reads
 * {@link activeCriteriaCount} for the trigger badge (same contract as
 * `ays-clinic-search-criteria`).
 *
 * One facet today ("Typ"); a second one is an additive change here plus a
 * counter update in {@link resourcePickerCriteriaActiveCount}.
 *
 * Note the deliberately missing `appendTo="body"` on the multi-select (same as
 * `ays-clinic-search-criteria`): a body-appended overlay opened *inside* a
 * popover reads as an outside click and would close the panel underneath it.
 */
@Component({
  selector: 'bd-resource-picker-filter',
  exportAs: 'bdResourcePickerFilter',
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <p-popover #popover>
      <div class="bd-resource-picker-filter">
        <div class="flex flex-col gap-2">
          <label class="text-sm" [attr.for]="typeInputId()">Typ</label>
          <!-- PrimeNG 22 treats an unset selectAll as "not all selected", so toggle-all could never deselect -->
          <p-multiselect
            [selectAll]="null"
            [inputId]="typeInputId()"
            [options]="typeOptions()"
            optionLabel="label"
            optionValue="value"
            variant="filled"
            display="chip"
            placeholder="Alle Typen"
            [filter]="showTypeFilter()"
            filterBy="label"
            [showHeader]="showTypeFilter()"
            [ngModel]="criteria().types"
            (ngModelChange)="patchTypes($event)"
            [pt]="{ root: { 'data-test': dataTestPrefix() + '-types' } }"
          />
        </div>

        @if (activeCriteriaCount() > 0) {
          <div class="flex justify-end">
            <p-button
              label="Zurücksetzen"
              severity="secondary"
              [text]="true"
              (onClick)="reset()"
              [pt]="{ root: { 'data-test': dataTestPrefix() + '-reset' } }"
            />
          </div>
        }
      </div>
    </p-popover>
  `,
  styles: [
    `
      .bd-resource-picker-filter {
        display: flex;
        flex-direction: column;
        gap: 1rem;
        min-width: 18rem;
        max-width: min(24rem, calc(100vw - 2rem));
      }
    `,
  ],
  imports: [FormsModule, Button, MultiSelect, Popover],
})
export class BdResourcePickerFilterComponent {
  /** Two-way bound active criteria. */
  public readonly criteria = model<ResourcePickerCriteria>({
    ...RESOURCE_PICKER_CRITERIA_EMPTY,
  });

  /**
   * Types the host allows at all; the "Typ" facet can only narrow within them.
   * `undefined` offers every shareable type except folders (which are
   * navigation, not a pickable type).
   */
  public readonly allowedTypes = input<readonly ShareableType[] | undefined>(
    undefined,
  );

  /** Prefix for each control's `data-test` id (hosts can disambiguate). */
  public readonly dataTestPrefix = input('bd-resource-picker');

  /** Number of facets currently set — hosts use it for the trigger badge. */
  public readonly activeCriteriaCount = computed(() =>
    resourcePickerCriteriaActiveCount(this.criteria()),
  );

  public readonly typeInputId = computed(
    () => `${this.dataTestPrefix()}-types-input`,
  );

  public readonly typeOptions = computed<TypeOption[]>(() => {
    const allowed =
      this.allowedTypes() ??
      (Object.keys(SHAREABLE_TYPE_DISPLAY_NAME) as ShareableType[]).filter(
        (type) => type !== ShareableType.FOLDER,
      );
    return [...allowed]
      .map((type) => ({
        label: SHAREABLE_TYPE_DISPLAY_NAME[type] ?? type,
        value: type,
      }))
      .sort((a, b) => a.label.localeCompare(b.label));
  });

  public readonly showTypeFilter = computed(
    () => this.typeOptions().length > FILTER_THRESHOLD,
  );

  private readonly _popover = viewChild.required(Popover);

  /** Open/close the panel relative to the event's trigger element. */
  public toggle(event: Event): void {
    this._popover().toggle(event);
  }

  /** Clear every facet. */
  public reset(): void {
    this.criteria.set({ ...RESOURCE_PICKER_CRITERIA_EMPTY });
  }

  protected patchTypes(types: ShareableType[]): void {
    this.criteria.update((current) => ({ ...current, types }));
  }
}
